import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #boundDonationClick = this.#handleDonationClick.bind(this);
  #boundDonationChange = this.#handleDonationChange.bind(this);
  /** @param {Event} event */
  #boundCartUpdate = (event) => {
    this.#handleCartUpdate(/** @type {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} */ (event));
  };
  /** @param {Event} event */
  #boundDiscountUpdate = (event) => {
    this.handleDiscountUpdate(/** @type {DiscountUpdateEvent} */ (event));
  };
  /** @param {Event} event */
  #boundQuantitySelectorUpdate = (event) => {
    this.#debouncedOnChange(/** @type {QuantitySelectorUpdateEvent} */ (event));
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#boundCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#boundDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#boundQuantitySelectorUpdate);
    this.addEventListener('click', this.#boundDonationClick);
    this.addEventListener('change', this.#boundDonationChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#boundCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#boundDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#boundQuantitySelectorUpdate);
    this.removeEventListener('click', this.#boundDonationClick);
    this.removeEventListener('change', this.#boundDonationChange);
  }

  /**
   * Handles donation button clicks using event delegation so controls keep working after section morphs.
   * @param {MouseEvent} event
   */
  async #handleDonationClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const presetButton = target.closest('[data-cart-donation-amount]');
    if (presetButton instanceof HTMLElement) {
      event.preventDefault();

      const variantId = presetButton.dataset.variantId;
      if (!variantId) return;

      await this.#addDonationItem({
        id: variantId,
        quantity: 1,
        properties: {
          _donation_type: 'product',
        },
      });
      return;
    }

    const customDonationButton = target.closest('[data-cart-donation-custom-submit]');
    if (customDonationButton instanceof HTMLElement) {
      event.preventDefault();

      const wrapper = customDonationButton.closest('[data-cart-donation]');
      const input = wrapper?.querySelector('[data-cart-donation-custom-input]');
      const error = wrapper?.querySelector('[data-cart-donation-error]');

      if (!(wrapper instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

      const minimumDonation = Number(wrapper.dataset.minimumDonation || 5);
      const donationAmount = Math.round(Number(input.value || 0) * 100);

      if (Number.isNaN(donationAmount) || donationAmount < minimumDonation * 100) {
        error?.classList.remove('hidden');
        return;
      }

      error?.classList.add('hidden');

      const variantId = input.dataset.variantId;
      if (!variantId) return;

      const existingLines = this.#parseLineNumbers(wrapper.dataset.customDonationLines);
      await this.#replaceDonationItems(existingLines, {
        id: variantId,
        quantity: donationAmount,
        properties: {
          _donation_type: 'product',
          _donation_mode: 'custom',
        },
      });
      return;
    }

    const continueButton = target.closest('[data-cart-gift-aid-continue]');
    if (continueButton instanceof HTMLElement) {
      event.preventDefault();

      const wrapper = continueButton.closest('[data-cart-donation]');
      if (!(wrapper instanceof HTMLElement)) return;

      await this.#saveGiftAidPreferences(wrapper);
      this.#closeGiftAidDialog(wrapper);
      return;
    }

    const closeButton = target.closest('[data-cart-gift-aid-close]');
    if (closeButton instanceof HTMLElement) {
      event.preventDefault();

      const wrapper = closeButton.closest('[data-cart-donation]');
      if (!(wrapper instanceof HTMLElement)) return;

      this.#closeGiftAidDialog(wrapper);
    }
  }

  /**
   * Handles donation checkbox changes.
   * @param {Event} event
   */
  async #handleDonationChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-cart-round-donation]')) {
      const wrapper = target.closest('[data-cart-donation]');
      if (!(wrapper instanceof HTMLElement)) return;

      const existingLines = this.#parseLineNumbers(wrapper.dataset.roundDonationLines);

      if (target.checked !== true) {
        await this.#removeLines(existingLines);
        return;
      }

      const variantId = target.dataset.variantId;
      const cartTotal = Number(target.dataset.cartTotal || 0);
      const roundedTotal = Math.ceil(cartTotal / 100) * 100;
      const donationAmount = roundedTotal - cartTotal;

      if (!variantId || donationAmount <= 0) return;

      await this.#replaceDonationItems(existingLines, {
        id: variantId,
        quantity: donationAmount,
        properties: {
          _donation_type: 'rounded',
        },
      });
      return;
    }

    if (target.matches('[data-cart-gift-aid-toggle]')) {
      const wrapper = target.closest('[data-cart-donation]');
      if (!(wrapper instanceof HTMLElement)) return;

      if (target.checked) {
        this.#openGiftAidDialog(wrapper);
      } else {
        const personalGiftAid = wrapper.querySelector('[data-cart-gift-aid-personal]');
        const noGiftAid = wrapper.querySelector('[data-cart-gift-aid-value][value="No"]');

        if (personalGiftAid instanceof HTMLInputElement) {
          personalGiftAid.checked = false;
        }

        if (noGiftAid instanceof HTMLInputElement) {
          noGiftAid.checked = true;
        }

        await this.#saveGiftAidPreferences(wrapper);
      }
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Adds a donation item to the cart and dispatches the theme cart update event.
   * @param {{ id: string; quantity: number; properties: Record<string, string>; }} config
   */
  async #addDonationItem(config) {
    this.#disableCartItems();

    const formData = new FormData();
    formData.append('id', config.id);
    formData.append('quantity', config.quantity.toString());

    for (const [key, value] of Object.entries(config.properties)) {
      formData.append(`properties[${key}]`, value);
    }

    const sections = this.#getSectionsToUpdate();
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const data = await response.json();

      if (data.status) {
        throw new Error(data.message || 'Failed to add donation item');
      }

      document.dispatchEvent(
        new CartAddEvent({}, config.id, {
          source: 'cart-items-component',
          itemCount: Number(config.quantity),
          variantId: config.id,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Removes one or more lines from the cart.
   * @param {number[]} lines
   */
  async #removeLines(lines) {
    if (lines.length === 0) return;

    const sortedLines = [...lines].sort((left, right) => right - left);
    for (const line of sortedLines) {
      await this.#updateCartLine(line, 0);
    }
  }

  /**
   * Replaces donation lines before adding the new donation item.
   * @param {number[]} existingLines
   * @param {{ id: string; quantity: number; properties: Record<string, string>; }} nextItem
   */
  async #replaceDonationItems(existingLines, nextItem) {
    await this.#removeLines(existingLines);
    await this.#addDonationItem(nextItem);
  }

  /**
   * Updates a cart line quantity and refreshes all cart sections.
   * @param {number} line
   * @param {number} quantity
   */
  async #updateCartLine(line, quantity) {
    this.#disableCartItems();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.#getSectionsToUpdate().join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponseText = JSON.parse(responseText);

      if (parsedResponseText.errors) {
        throw new Error(parsedResponseText.errors);
      }

      const updatedSection = parsedResponseText.sections?.[this.sectionId];
      const newSectionHTML = updatedSection
        ? new DOMParser().parseFromString(updatedSection, 'text/html')
        : null;
      const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.#updateQuantitySelectors(parsedResponseText);

      document.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Saves Gift Aid preferences as cart attributes.
   * @param {HTMLElement} wrapper
   */
  async #saveGiftAidPreferences(wrapper) {
    const giftAidValue = wrapper.querySelector('[data-cart-gift-aid-value]:checked');
    const personalGiftAid = wrapper.querySelector('[data-cart-gift-aid-personal]');
    const giftAidToggle = wrapper.querySelector('[data-cart-gift-aid-toggle]');

    const attributes = {
      'Gift Aid': giftAidValue instanceof HTMLInputElement ? giftAidValue.value : 'No',
      'Personal Gift Aid': personalGiftAid instanceof HTMLInputElement && personalGiftAid.checked ? 'Yes' : '',
    };

    const body = JSON.stringify({
      attributes,
      sections: this.#getSectionsToUpdate(),
      sections_url: window.location.pathname,
    });

    this.#disableCartItems();

    try {
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const data = await response.json();
      const updatedSection = data.sections?.[this.sectionId];
      const newSectionHTML = updatedSection
        ? new DOMParser().parseFromString(updatedSection, 'text/html')
        : null;
      const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      if (giftAidToggle instanceof HTMLInputElement) {
        giftAidToggle.checked = attributes['Gift Aid'] === 'Yes';
      }

      document.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Opens the Gift Aid dialog for a donation wrapper.
   * @param {HTMLElement} wrapper
   */
  #openGiftAidDialog(wrapper) {
    const dialogComponent = wrapper.querySelector('[data-cart-gift-aid-dialog]');
    this.#bindGiftAidDialogClose(wrapper);

    if (dialogComponent && 'showDialog' in dialogComponent && typeof dialogComponent.showDialog === 'function') {
      dialogComponent.showDialog();
    }
  }

  /**
   * Closes the Gift Aid dialog for a donation wrapper.
   * @param {HTMLElement} wrapper
   */
  #closeGiftAidDialog(wrapper) {
    const dialogComponent = wrapper.querySelector('[data-cart-gift-aid-dialog]');
    if (dialogComponent && 'closeDialog' in dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
    }
  }

  /**
   * Binds Gift Aid dialog close handling once per dialog instance.
   * @param {HTMLElement} wrapper
   */
  #bindGiftAidDialogClose(wrapper) {
    const dialogComponent = wrapper.querySelector('[data-cart-gift-aid-dialog]');
    if (!(dialogComponent instanceof HTMLElement)) return;

    if (dialogComponent.dataset.giftAidCloseBound === 'true') return;

    dialogComponent.dataset.giftAidCloseBound = 'true';
    dialogComponent.addEventListener('dialog:close', () => {
      this.#syncGiftAidToggle(wrapper);
    });
  }

  /**
   * Keeps the opener checkbox aligned with the current Gift Aid selection.
   * @param {HTMLElement} wrapper
   */
  #syncGiftAidToggle(wrapper) {
    const giftAidToggle = wrapper.querySelector('[data-cart-gift-aid-toggle]');
    const selectedGiftAidValue = wrapper.querySelector('[data-cart-gift-aid-value]:checked');

    if (!(giftAidToggle instanceof HTMLInputElement)) return;

    giftAidToggle.checked =
      selectedGiftAidValue instanceof HTMLInputElement && selectedGiftAidValue.value === 'Yes';
  }

  /**
   * Returns all cart sections that should be refreshed for cart mutations.
   * @returns {string[]}
   */
  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionsToUpdate);
  }

  /**
   * Parses a comma-separated line number list from a data attribute.
   * @param {string | undefined} value
   * @returns {number[]}
   */
  #parseLineNumbers(value) {
    if (!value) return [];

    return value
      .split(',')
      .map((line) => Number(line.trim()))
      .filter((line) => Number.isInteger(line) && line > 0);
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
